import React, { useState, useEffect } from 'react';
import MediaLibrary from './MediaLibrary';
import MediaUploader from './MediaUploader';
import MediaItem from './MediaItem';
import { useAuth } from '../helpers/AuthContext';

// Helper: get selected media by value
function getSelectedMedia(media, value) {
    return (media || []).find(item => ((item.id || '').toString() === (value || '').toString()));
}

function normalizeValue(value) {
    return value ?? '';
}

// Main Mediapicker component
function Mediapicker({
    name = 'fileId',
    initialValue = '',
    accept = '',
    onChange,
    label,
    placeholder,
    required
}) {
    const { fetch } = useAuth();
    const [media, setMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showLibrary, setShowLibrary] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [value, setValue] = useState(normalizeValue(initialValue));

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetch(`/account/media/library.json`)
            .then(res => {
                if (!res.ok) throw new Error('Failed to fetch media library');
                return res.json();
            })
            .then(data => setMedia(data))
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [fetch]);

    useEffect(() => {
        setValue(normalizeValue(initialValue));
    }, [initialValue]);

    const selectedMedia = getSelectedMedia(media, value);

    // Filter media by accept
    const filteredMedia = (media || []).filter(file => {
        if (accept && accept.length > 0) {
            const allowedMimetypes = accept.split(',').map(item => item.trim());
            return allowedMimetypes.includes(file.mimetype);
        }
        return true;
    }).filter(file => !(file.metadata && file.metadata.isHidden));

    // Handlers
    const handleRemove = () => {
        setValue('');
        if (onChange) onChange('');
    };
    const handlePick = (id) => {
        setValue(id);
        if (onChange) onChange(id);
        setShowLibrary(false);
    };
    const handleToggleLibrary = (e) => {
        e.preventDefault();
        setShowLibrary(v => !v);
        setShowUpload(false);
    };
    const handleToggleUpload = (e) => {
        e.preventDefault();
        setShowUpload(v => !v);
        setShowLibrary(false);
    };

    return (
        <div className="mediapicker">
            <input
                className="mediapicker-hidden-value"
                type="hidden"
                name={name}
                value={value}
                placeholder="hidden mediapicker id"
            />
            {loading && <div>Loading media library...</div>}
            {error && <div className="error">{error}</div>}
            {selectedMedia ? (
                <MediaItem
                    file={selectedMedia}
                    isActive={true}
                    showRemove={true}
                    onRemove={handleRemove}
                    className="selected-media"
                />
            ) : (
                <div className="selected-media" style={{ display: 'none' }}>
                    <a href="#"> <img alt="" /> </a>
                </div>
            )}

            <a href="#" className="toggle-media-library" onClick={handleToggleLibrary}>Select Media</a> or{' '}
            <a href="#" className="toggle-upload" onClick={handleToggleUpload}>Upload New</a>

            {showLibrary && (
                <MediaLibrary
                    media={filteredMedia}
                    value={value}
                    accept={accept}
                    onPick={handlePick}
                />
            )}

            {showUpload && (
                <MediaUploader
                    accept={accept}
                    onUploadSuccess={(id, file) => {
                        setValue(id);
                        if (onChange) onChange(id);
                        setShowUpload(false);
                        setLoading(true);
                        fetch(`${DASHBOARD_URL}/account/media/library.json`)
                            .then(res => res.ok ? res.json() : [])
                            .then(data => setMedia(data))
                            .finally(() => setLoading(false));
                    }}
                />
            )}
        </div>
    );
}

export default Mediapicker;
